import { useCallback, useEffect, useRef, useState } from 'react';
import { InsertDriveFileOutlined, StickyNote2Outlined, TaskOutlined } from '@mui/icons-material';
import { DocumentReferenceObjectView, DocumentReferencesView } from 'api';
import DRAWER, { TAB_IDS } from 'common/constants/drawer/Drawer.constants';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useDrawerContext } from 'components/Drawer/draggable-drawer/DraggableDrawer.context';
import NotesDrawerTabTitle from 'components/Drawer/notes-drawer-tab-title/NotesDrawerTabTitle';
import NavigationConfig from 'components/Drawer/tab-drawer/models/navigation-config.interface';
import TabDrawer from 'components/Drawer/tab-drawer/TabDrawer';
import { Widgets } from 'components/Widgets';
import { useLayoutContext } from 'context/LayoutContext';
import { useSessionStorage } from 'services/hooks';
import useDocumentTabs from './hooks/useDocumentTabs';
import WidgetDrawerProps from './WidgetDrawer.props';
import { EventType, useEventSubscription } from '../../../context';
import { NavigateToDocumentReferencePayload } from '../../../context/EventBusContext/EventBusContext.type';
import { setPendingNavigation } from '../document-preview-drawer-tab/xlsx-viewer/pendingNavigation';

const ICON_SIZE = '1rem';
const ACTIVE_TAB_STORAGE_SUFFIX = 'active-tab-id';

const WidgetDrawer = ({
  documentReferencesProps,
  documentReferenceHeaderContent,
  documentSourceProps,
  notesProps,
  spreadsheetNotesProps,
  applicationContext,
  contentWrapperSx,
  headerContentSx,
  documentTabCustomActions,
  documentApplicationStorageKey,
}: WidgetDrawerProps) => {
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { isExportMode } = useLayoutContext();
  const { expandDrawer } = useDrawerContext();
  const [navigationConfig, setNavigationConfig] = useState<NavigationConfig[]>([]);

  const storageKey = documentApplicationStorageKey ?? `${companyInfo?.id}-${applicationContext}-doc-navigation-config`;
  const activeTabStorageKey = `${storageKey}-${ACTIVE_TAB_STORAGE_SUFFIX}`;
  const [activeTabId, setActiveTabId] = useSessionStorage<string | null>(activeTabStorageKey, null, {
    migrateFromLocalOnce: true,
  });

  const { documentTabConfigs, openDocumentTab, openDocumentById, closeDocumentTab, openDocumentIds } = useDocumentTabs({
    storageKey,
    companyId: companyInfo?.id?.toString() ?? '',
    documentTabCustomActions,
    documentSourceProps,
  });

  const handleDocumentClick = useCallback(
    (document: DocumentReferencesView, reference: DocumentReferenceObjectView) => {
      const docId = openDocumentTab(document, reference);
      setActiveTabId(docId);
      expandDrawer();
    },
    [openDocumentTab, setActiveTabId, expandDrawer]
  );

  const handleDocumentClickRef = useRef(handleDocumentClick);
  handleDocumentClickRef.current = handleDocumentClick;

  const stableHandleDocumentClick = useCallback(
    (document: DocumentReferencesView, reference: DocumentReferenceObjectView) => {
      handleDocumentClickRef.current(document, reference);
    },
    []
  );

  const handleTabClose = useCallback(
    (tabId: string) => {
      closeDocumentTab(tabId);

      if (activeTabId === tabId) {
        const remaining = navigationConfig.filter(t => t.uniqueId !== tabId);
        const fallback = remaining.find(t => t.uniqueId === TAB_IDS.TABLE_DOCUMENT_REFERENCES) ?? remaining[0];
        setActiveTabId(fallback?.uniqueId ?? null);
      }
    },
    [closeDocumentTab, activeTabId, navigationConfig, setActiveTabId]
  );

  const handleTabCloseRef = useRef(handleTabClose);
  handleTabCloseRef.current = handleTabClose;

  const stableHandleTabClose = useCallback((tabId: string) => {
    handleTabCloseRef.current(tabId);
  }, []);

  useEventSubscription(
    EventType.NAVIGATE_TO_DOCUMENT_REFERENCE,
    (payload: NavigateToDocumentReferencePayload) => {
      const docId = payload.documentReferenceId.toString();
      openDocumentById(docId);
      setPendingNavigation(payload);
      setActiveTabId(docId);
      if (payload.expandDrawer) {
        expandDrawer();
      }
    }
  );

  // Build navigation config
  useEffect(() => {
    const config: NavigationConfig[] = [];

    if (notesProps) {
      config.push({
        tabContent: <Widgets notesProps={notesProps} />,
        tabTitle: (
          <NotesDrawerTabTitle
            pageType={notesProps.pageType}
            pageTypeKey={notesProps.pageTypeKey}
            pageTypeId={notesProps.pageTypeId}
            notesFallback={notesProps.notes}
          />
        ),
        uniqueId: TAB_IDS.TABLE_NOTES,
        icon: <StickyNote2Outlined sx={{ height: ICON_SIZE, width: ICON_SIZE }} />,
      });
    }

    if (spreadsheetNotesProps) {
      config.push({
        tabContent: <Widgets spreadsheetNotesProps={spreadsheetNotesProps} />,
        tabTitle: DRAWER.TABS.SPREADSHEET_NOTES,
        uniqueId: TAB_IDS.TABLE_SPREADSHEET_NOTES,
        icon: <TaskOutlined sx={{ height: ICON_SIZE, width: ICON_SIZE }} />,
      });
    }

    if (documentReferencesProps) {
      config.push({
        tabContent: (
          <Widgets
            documentReferencesProps={documentReferencesProps}
            documentReferenceHeaderContent={documentReferenceHeaderContent}
            documentSourceProps={documentSourceProps}
            onDocumentClick={stableHandleDocumentClick}
          />
        ),
        tabTitle: DRAWER.TABS.DOCUMENT_REFERENCES,
        uniqueId: TAB_IDS.TABLE_DOCUMENT_REFERENCES,
        icon: <InsertDriveFileOutlined sx={{ height: ICON_SIZE, width: ICON_SIZE }} />,
      });
    }

    const docTabs = documentTabConfigs.map(tab => ({
      ...tab,
      onClose: stableHandleTabClose,
    }));

    setNavigationConfig([...config, ...docTabs]);
  }, [
    notesProps,
    spreadsheetNotesProps,
    documentReferencesProps,
    documentReferenceHeaderContent,
    documentSourceProps,
    documentTabConfigs,
    stableHandleDocumentClick,
    stableHandleTabClose,
  ]);

  useEffect(() => {
    if (!navigationConfig.length || !activeTabId) {
      return;
    }

    const activeTabExists = navigationConfig.some(tab => tab.uniqueId === activeTabId);
    const isOpeningDocumentTab = openDocumentIds.includes(activeTabId);
    if (activeTabExists || isOpeningDocumentTab) {
      return;
    }

    const fallbackTab = navigationConfig.find(tab => tab.uniqueId === TAB_IDS.TABLE_DOCUMENT_REFERENCES) ?? navigationConfig[0];
    setActiveTabId(fallbackTab?.uniqueId ?? null);
  }, [navigationConfig, activeTabId, openDocumentIds, setActiveTabId]);

  const isDocumentPreviewActive = activeTabId != null && !Object.values(TAB_IDS).includes(activeTabId as TAB_IDS);

  const effectiveContentSx = isDocumentPreviewActive
    ? { ...((contentWrapperSx ?? {}) as Record<string, unknown>), paddingLeft: '1rem' }
    : contentWrapperSx;

  if (isExportMode || !navigationConfig.length) {
    return null;
  }

  return (
    <TabDrawer
      navigationConfig={navigationConfig}
      activeTabId={activeTabId}
      onTabSelect={setActiveTabId}
      contentWrapperSx={effectiveContentSx}
      headerContentSx={headerContentSx}
    />
  );
};

export default WidgetDrawer;
